import apiService from './api';
import { SampleDataTransformer } from './sampleDataTransformer';

// Backend enums (matching the production backend - ENHANCED WORKFLOW)
export enum SampleStatus {
  // Initial Reception
  RECEPTION = 'RECEPTION',
  
  // Mechanical Preparation (if required by selected methods)
  PREPARATION_MECANIQUE = 'PREPARATION_MECANIQUE',
  
  // TEMPORARY: Keep old status for backward compatibility during transition
  ANALYSE_CHIMIQUE = 'ANALYSE_CHIMIQUE',
  
  // Laboratory-specific workflows (method-dependent routing)
  LABORATOIRE_SAA = 'LABORATOIRE_SAA',
  LABORATOIRE_SAA_PREP_CHIMIQUE = 'LABORATOIRE_SAA_PREP_CHIMIQUE',
  LABORATOIRE_SAA_ANALYSE = 'LABORATOIRE_SAA_ANALYSE',
  
  LABORATOIRE_ICP = 'LABORATOIRE_ICP',
  LABORATOIRE_ICP_PREP_CHIMIQUE = 'LABORATOIRE_ICP_PREP_CHIMIQUE',
  LABORATOIRE_ICP_ANALYSE = 'LABORATOIRE_ICP_ANALYSE',
  
  LABORATOIRE_AU = 'LABORATOIRE_AU',
  LABORATOIRE_AU_PREP_CHIMIQUE = 'LABORATOIRE_AU_PREP_CHIMIQUE',
  LABORATOIRE_AU_ANALYSE = 'LABORATOIRE_AU_ANALYSE',
  
  LABORATOIRE_VOLUMETRIE = 'LABORATOIRE_VOLUMETRIE',
  LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE = 'LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE',
  LABORATOIRE_VOLUMETRIE_ANALYSE = 'LABORATOIRE_VOLUMETRIE_ANALYSE',
  
  // Physico-Chimique Laboratory (no prep chimique - direct to analyse)
  LABORATOIRE_PHYSICO_CHIMIQUE = 'LABORATOIRE_PHYSICO_CHIMIQUE',
  LABORATOIRE_PHYSICO_CHIMIQUE_ANALYSE = 'LABORATOIRE_PHYSICO_CHIMIQUE_ANALYSE',
  
  // Post-analysis workflow
  VALIDATION_SERVICE = 'VALIDATION_SERVICE',
  GENERATION_CERTIFICAT = 'GENERATION_CERTIFICAT',
  PRET = 'PRET',
  
  // Exception states
  REJETE = 'REJETE',
  EN_ATTENTE = 'EN_ATTENTE',
}

export enum SampleType {
  MINERAI = 'MINERAI',
  CONCENTRE = 'CONCENTRE',
  STERILE = 'STERILE',
  SOL = 'SOL',
  EAU = 'EAU',
  CAROTTE = 'CAROTTE',
  ECLAT = 'ECLAT',
  TOUT_VENANT = 'TOUT_VENANT',
}

export enum Priority {
  NORMALE = 'NORMALE',
  ELEVEE = 'ELEVEE',
  URGENTE = 'URGENTE',
  CONTROLE_QUALITE = 'CONTROLE_QUALITE',
}

// Interfaces matching the backend
export interface LocationCoordinates {
  latitude: number;
  longitude: number;
  elevation?: number;
  utm_x?: number;
  utm_y?: number;
  utm_zone?: string;
}

export interface SampleMetadata {
  poids_kg?: number;
  nombre_sacs?: number;
  etat_echantillon?: string;
  profondeur_m?: number;
  reference_forage?: string;
  interval_de?: number;
  interval_a?: number;
  description?: string;
  collecteur?: string;
  date_collecte?: Date;
  methode_collecte?: 'CANAL' | 'GRAB' | 'COMPOSITE' | 'SPLIT';
  conditions_meteo?: string;
  equipement_utilise?: string;
  photos?: string[];
  certificat_transport?: string;
  formation_geologique?: string;
  type_roche?: string;
  alteration?: string;
  mineralisation_visible?: string;
  commentaires_geologue?: string;
  analyses_demandees?: string[];
  conditions_speciales?: string;
  temperature_stockage?: number;
  [key: string]: any;
}

export interface Client {
  id: string;
  tenant_id: string;
  code: string;
  company_name: string;
  client_type: string;
  contact_person?: string;
  email: string;
  phone: string;
  address: string;
  city?: string;
  postal_code?: string;
  country: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Sample {
  id: string;
  sample_id: string;
  external_id?: string;
  tenant_id: string;
  client_id: string;
  client: Client;
  nom_projet?: string;
  site_minier?: string;
  type_echantillon: SampleType;
  statut: SampleStatus;
  priorite: Priority;
  coordonnees?: LocationCoordinates;
  localisation_stockage?: string;
  metadonnees?: SampleMetadata;
  photos?: string[];
  qr_code: string;
  code_barres: string;
  date_reception?: string;
  date_debut_preparation?: string;
  date_fin_preparation?: string;
  date_debut_analyse?: string;
  date_fin_analyse?: string;
  date_pret?: string;
  date_creation: string;
  date_modification: string;
  cree_par: string;
  assigne_a?: string;
  commentaires?: string;
  tags?: string[];
  batch_id?: string;
}

export interface SampleListResponse {
  samples: Sample[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SampleFilters {
  status?: SampleStatus[];
  type?: SampleType[];
  priority?: Priority[];
  clientId?: string;
  assignedTo?: string;
  dateFrom?: Date;
  dateTo?: Date;
  search?: string;
  laboratory?: string; // For BGM multi-lab filtering
  validationSourceLaboratory?: string; // For validation sample filtering by source lab
}

export interface SampleStatistics {
  total: number;
  par_statut: Record<SampleStatus, number>;
  par_type: Record<SampleType, number>;
  par_priorite: Record<Priority, number>;
  en_retard?: number;
  termines_aujourd_hui?: number;
  moyenne_duree_jours?: number;
}

interface QRCodeResponse {
  sampleId: string;
  complex?: string;
  simple?: string;
  format: string;
}

class SamplesService {
  async getSamples(
    page: number = 1,
    limit: number = 50,
    filters?: SampleFilters
  ): Promise<SampleListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    // Add filters to params
    if (filters?.status?.length) {
      filters.status.forEach(status => params.append('status', status));
    }
    if (filters?.type?.length) {
      filters.type.forEach(type => params.append('type', type));
    }
    if (filters?.priority?.length) {
      filters.priority.forEach(priority => params.append('priority', priority));
    }
    if (filters?.clientId) {
      params.append('clientId', filters.clientId);
    }
    if (filters?.assignedTo) {
      params.append('assignedTo', filters.assignedTo);
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }
    if (filters?.dateFrom) {
      params.append('dateFrom', filters.dateFrom.toISOString());
    }
    if (filters?.dateTo) {
      params.append('dateTo', filters.dateTo.toISOString());
    }
    if (filters?.laboratory) {
      params.append('laboratory', filters.laboratory);
    }
    if (filters?.validationSourceLaboratory) {
      params.append('validationSourceLaboratory', filters.validationSourceLaboratory);
    }

    return apiService.get<SampleListResponse>(`/samples?${params.toString()}`);
  }

  // 🔓 ADMIN-ONLY: Get all samples bypassing laboratory access filtering
  async getSamplesAdmin(
    page: number = 1,
    limit: number = 50,
    filters?: SampleFilters
  ): Promise<SampleListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    // Add filters to params
    if (filters?.status?.length) {
      filters.status.forEach(status => params.append('status', status));
    }
    if (filters?.type?.length) {
      filters.type.forEach(type => params.append('type', type));
    }
    if (filters?.priority?.length) {
      filters.priority.forEach(priority => params.append('priority', priority));
    }
    if (filters?.clientId) {
      params.append('clientId', filters.clientId);
    }
    if (filters?.assignedTo) {
      params.append('assignedTo', filters.assignedTo);
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }
    if (filters?.dateFrom) {
      params.append('dateFrom', filters.dateFrom.toISOString());
    }
    if (filters?.dateTo) {
      params.append('dateTo', filters.dateTo.toISOString());
    }
    if (filters?.laboratory) {
      params.append('laboratory', filters.laboratory);
    }
    if (filters?.validationSourceLaboratory) {
      params.append('validationSourceLaboratory', filters.validationSourceLaboratory);
    }

    // 🚀 Use admin-all endpoint to bypass laboratory access filtering
    return apiService.get<SampleListResponse>(`/samples/admin-all?${params.toString()}`);
  }

  async getSample(id: string): Promise<Sample> {
    return apiService.get<Sample>(`/samples/${id}`);
  }

  async getSampleBySampleId(sampleId: string): Promise<Sample> {
    return apiService.get<Sample>(`/samples/sample-id/${sampleId}`);
  }

  async getStatistics(
    cacheBuster?: string,
    options?: {
      excludeCompleted?: boolean;
      dateFrom?: Date;
      dateTo?: Date;
      activeSamplesOnly?: boolean;
    }
  ): Promise<SampleStatistics> {
    const params = new URLSearchParams();
    if (cacheBuster) params.append('_', cacheBuster);
    if (options?.excludeCompleted) params.append('excludeCompleted', 'true');
    if (options?.dateFrom) params.append('dateFrom', options.dateFrom.toISOString());
    if (options?.dateTo) params.append('dateTo', options.dateTo.toISOString());
    if (options?.activeSamplesOnly) params.append('activeSamplesOnly', 'true');
    
    const queryString = params.toString();
    const url = queryString ? `/samples/statistics?${queryString}` : '/samples/statistics';
    
    return apiService.get<SampleStatistics>(url);
  }

  async scanQRCode(qrData: string): Promise<Sample> {
    return apiService.post<Sample>('/samples/scan-qr', { qrData });
  }

  async createSample(sampleData: any): Promise<Sample> {
    console.log('🔄 Creating sample with data:', sampleData);
    
    // Ensure analyses_demandees is at the top level for workflow routing
    if (sampleData.metadonnees?.analyses_demandees && !sampleData.analyses_demandees) {
      sampleData.analyses_demandees = sampleData.metadonnees.analyses_demandees;
    }
    
    return apiService.post<Sample>('/samples', sampleData);
  }

  async updateSample(id: string, sampleData: Partial<Sample>): Promise<Sample> {
    return apiService.patch<Sample>(`/samples/${id}`, sampleData);
  }

  async updateSampleStatus(
    id: string,
    status: SampleStatus,
    reason?: string,
    validationSourceLaboratory?: string
  ): Promise<Sample> {
    // Use regular PATCH endpoint instead of /status endpoint due to access control issues
    const updateData: any = { 
      statut: status, 
      commentaires: reason 
    };
    
    // If moving to validation and source laboratory is provided, include it
    if (status === SampleStatus.VALIDATION_SERVICE && validationSourceLaboratory) {
      updateData.validation_source_laboratory = validationSourceLaboratory;
    }
    
    return apiService.patch<Sample>(`/samples/${id}`, updateData);
  }

  async assignSample(id: string, assignedTo: string): Promise<Sample> {
    return apiService.patch<Sample>(`/samples/${id}/assign`, { assignedTo });
  }

  async deleteSample(id: string): Promise<{ message: string }> {
    return apiService.delete<{ message: string }>(`/samples/${id}`);
  }

  async generateQRCode(id: string, format: 'simple' | 'complex' = 'simple'): Promise<Blob> {
    try {
      console.log('🔄 Requesting QR code from backend for sample:', id);
      
      // Get the QR code data from backend
      const qrData = await apiService.get<any>(`/samples/${id}/qr-code?format=${format}`);
      console.log('📋 QR code response from backend:', qrData);
      
      // Check if we have any QR code data
      let base64Data = null;
      
      if (qrData.qrCode) {
        base64Data = qrData.qrCode;
        console.log('✅ Using qrCode property from backend response');
      } else if (qrData.complex) {
        base64Data = qrData.complex;
        console.log('✅ Using complex QR code format');
      } else if (qrData.simple) {
        base64Data = qrData.simple;
        console.log('✅ Using simple QR code format');
      } else if (qrData.qrCodeBase64) {
        base64Data = qrData.qrCodeBase64;
        console.log('✅ Using qrCodeBase64 format');
      } else if (qrData.data) {
        base64Data = qrData.data;
        console.log('✅ Using data format');
      } else if (typeof qrData === 'string') {
        base64Data = qrData;
        console.log('✅ Using direct string response');
      }
      
      if (!base64Data) {
        console.error('❌ No QR code data found in response. Available keys:', Object.keys(qrData));
        throw new Error(`No QR code data received. Response: ${JSON.stringify(qrData)}`);
      }
      
      console.log('🔄 Converting base64 to blob...');
      
      // Handle different base64 formats
      let base64String = base64Data;
      
      // Remove data URL prefix if present
      if (base64String.startsWith('data:image/')) {
        const commaIndex = base64String.indexOf(',');
        if (commaIndex !== -1) {
          base64String = base64String.substring(commaIndex + 1);
        }
      }
      
      // Convert base64 to blob
      try {
        const binaryString = atob(base64String);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'image/png' });
        
        console.log('✅ QR code blob created successfully, size:', blob.size, 'bytes');
        return blob;
        
      } catch (decodeError: any) {
        console.error('❌ Error decoding base64:', decodeError);
        throw new Error(`Failed to decode base64 QR code: ${decodeError.message}`);
      }
      
    } catch (error: any) {
      console.error('❌ Error generating QR code:', error);
      
      // Provide more specific error messages
      if (error.message?.includes('404')) {
        throw new Error('Sample not found - cannot generate QR code');
      } else if (error.message?.includes('401')) {
        throw new Error('Authentication required - please login again');
      } else if (error.message?.includes('Failed to fetch')) {
        throw new Error('Cannot connect to server - check if backend is running');
      } else {
        throw new Error(`QR code generation failed: ${error.message}`);
      }
    }
  }

  async getQRCodeData(id: string, format: 'simple' | 'complex' = 'simple'): Promise<any> {
    return apiService.get(`/samples/${id}/qr-code?format=${format}`);
  }

  async generateLabel(id: string): Promise<Blob> {
    const response = await fetch(`${apiService['baseURL']}/samples/${id}/label`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to generate label');
    }
    
    return response.blob();
  }

  // Certificate generation methods removed - to be rebuilt from scratch

  // Get samples with detailed tracking info
  async getSamplesWithTracking(): Promise<any[]> {
    return apiService.get('/samples/with-tracking');
  }

  // **NEW: Workflow Analytics API**
  async getWorkflowAnalytics(): Promise<{
    workflow_velocity: {
      samples_per_day: number;
      avg_turnaround_hours: number;
      completion_rate: number;
    };
    bottleneck_analysis: {
      current_bottlenecks: Array<{
        stage: string;
        sample_count: number;
        avg_duration_hours: number;
        severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
      }>;
      recommendations: string[];
    };
    historical_trends: {
      last_30_days: Array<{
        date: string;
        samples_completed: number;
        avg_turnaround: number;
      }>;
    };
  }> {
    return apiService.get('/analytics/workflow');
  }

  // **NEW: Quality Control Analytics**
  async getQualityAnalytics(): Promise<{
    control_charts: Array<{
      laboratory: string;
      element: string;
      recent_points: Array<{
        date: string;
        value: number;
        in_control: boolean;
      }>;
    }>;
    recovery_rates: Record<string, number>;
    contamination_events: Array<{
      date: string;
      laboratory: string;
      sample_id: string;
    }>;
  }> {
    return apiService.get('/analytics/quality');
  }

  // **NEW: Sample Processing Analytics**
  async getSampleProcessingMetrics(startDate: string, endDate: string): Promise<{
    laboratoryMetrics: {
      SAA: { samplesProcessed: number; avgProcessingTime: number };
      ICP: { samplesProcessed: number; avgProcessingTime: number };
      AU: { samplesProcessed: number; avgProcessingTime: number };
      VOLUMETRIE: { samplesProcessed: number; avgProcessingTime: number };
    };
    serviceTimeMetrics: {
      prepChimiqueICP: { avgTime: number; count: number };
      analysteICP: { avgTime: number; count: number };
      prepChimiqueSAA: { avgTime: number; count: number };
      analysteSAA: { avgTime: number; count: number };
      prepChimiqueAU: { avgTime: number; count: number };
      analysteAU: { avgTime: number; count: number };
      prepChimiqueVOLUMETRIE: { avgTime: number; count: number };
      analysteVOLUMETRIE: { avgTime: number; count: number };
      validation: { avgTime: number; count: number };
      certificateGeneration: { avgTime: number; count: number };
    };
  }> {
    const params = new URLSearchParams({ startDate, endDate });
    return apiService.get(`/analytics/sample-processing?${params.toString()}`);
  }

  // **NEW: SAFE PREDICTIVE ANALYTICS - Step 4** 🔮
  async getPredictiveAnalytics(): Promise<{
    completion_predictions: Array<{
      sample_id: string;
      predicted_completion: Date;
      confidence_level: number;
      risk_factors: string[];
    }>;
    equipment_health: Array<{
      equipment_id: string;
      health_score: number;
      maintenance_due: Date;
      failure_risk: 'LOW' | 'MEDIUM' | 'HIGH';
    }>;
    workload_forecast: Array<{
      date: string;
      predicted_samples: number;
      capacity_utilization: number;
      bottleneck_risk: number;
    }>;
  } | null> {
    try {
      return await apiService.get('/analytics/predictive');
    } catch (error) {
      console.warn('Predictive analytics not available yet:', error);
      return null; // Safe fallback - won't break existing app
    }
  }

  // **NEW: SAFE HISTORICAL TRENDS - Step 4** 📈
  async getHistoricalTrends(days: number = 30): Promise<{
    throughput_history: Array<{
      date: string;
      samples_received: number;
      samples_completed: number;
      avg_turnaround: number;
    }>;
    quality_trends: Array<{
      date: string;
      error_rate: number;
      rework_rate: number;
      client_satisfaction: number;
    }>;
    efficiency_trends: Record<string, Array<{
      date: string;
      efficiency_score: number;
      utilization: number;
    }>>;
  } | null> {
    try {
      return await apiService.get(`/analytics/historical?days=${days}`);
    } catch (error) {
      console.warn('Historical trends not available yet:', error);
      return null; // Safe fallback
    }
  }

  // **NEW: SAFE CLIENT PERFORMANCE ANALYTICS - Step 6** 👥
  async getClientAnalytics(): Promise<{
    total_samples: number;
    completion_rate: number;
    avg_turnaround_days: number;
    monthly_revenue: number;
    clients: Array<{
      name: string;
      samples_count: number;
      completion_rate: number;
      avg_turnaround: number;
      profitability: number;
    }>;
  } | null> {
    try {
      return await apiService.get('/analytics/clients');
    } catch (error) {
      console.warn('Client analytics not available yet:', error);
      return null; // Safe fallback
    }
  }

  // 🚀 PERFORMANCE: Bulk operations for batch processing
  async updateSampleBatch(
    sampleIds: string[],
    updates: any,
    statusUpdate?: { status: SampleStatus; comment: string }
  ): Promise<{ updated: number; errors: string[] }> {
    return await apiService.post('/samples/batch/update', {
      sampleIds,
      updates,
      statusUpdate
    });
  }

  async bulkUpdateSampleMetadata(
    sampleIds: string[],
    metadataUpdates: any
  ): Promise<{ updated: number; errors: string[] }> {
    return await this.updateSampleBatch(sampleIds, metadataUpdates);
  }

  async bulkUpdateSampleStatus(
    sampleIds: string[],
    newStatus: SampleStatus,
    comment: string
  ): Promise<{ updated: number; errors: string[] }> {
    return await this.updateSampleBatch(sampleIds, {}, {
      status: newStatus,
      comment
    });
  }

  async saveBatchPreparation(
    samples: { id: string; sample_id: string }[],
    preparationData: any,
    nextStatus: SampleStatus,
    comment: string
  ): Promise<{ updated: number; errors: string[] }> {
    const sampleIds = samples.map(s => s.id);
    
    // Update metadata and status in one call
    return await this.updateSampleBatch(
      sampleIds,
      preparationData,
      { status: nextStatus, comment }
    );
  }

  async completeBatchAnalysis(
    samples: { id: string; sample_id: string }[],
    analysisData: any,
    nextStatus: SampleStatus,
    comment: string
  ): Promise<{ updated: number; errors: string[] }> {
    const sampleIds = samples.map(s => s.id);
    
    // Update analysis results and status in one call
    return await this.updateSampleBatch(
      sampleIds,
      analysisData,
      { status: nextStatus, comment }
    );
  }
}

export const samplesService = new SamplesService();

// 🆕 NEW: QR Code Generation Service
export const generateQRCode = async (sampleId: string): Promise<Blob> => {
  try {
    // Get sample details for QR data
    const sample = await samplesService.getSample(sampleId);
    
    const qrData = JSON.stringify({
      sample_id: sample.sample_id,
      external_id: sample.external_id,
      client_id: sample.client_id,
      type_echantillon: sample.type_echantillon,
      lab_reference: sample.sample_id,
      client_reference: sample.external_id,
      created_at: sample.date_creation,
      status: sample.statut,
      lab: 'AFRILAB_MINING',
      type: 'SAMPLE_QR',
      url: `${window.location.origin}/samples/${sampleId}`
    });
    
    // Generate QR code locally (works offline)
    const QRCode = (await import('qrcode')).default;
    const dataUrl = await QRCode.toDataURL(qrData, {
      width: 400,
      margin: 0,
      errorCorrectionLevel: 'M',
      color: { dark: '#000000', light: '#ffffff' },
    });
    const res = await fetch(dataUrl);
    return await res.blob();
  } catch (error) {
    console.error('Error generating QR code:', error);
    throw error;
  }
};

export default samplesService; 